import { ArrowRight } from "lucide-react";
import AttachIcon from "../ui/icons/AttachIcon";
import LockIcon from "../ui/icons/LockIcon";
import SourceSelectPopover from "@/components/ai/source-select-popover";
import AttachBox from "@/components/ai/attach-box";
import JobTag from "@/components/ai/job-tag";
import { useAiAgentStore } from "@/stores/ai-agent";
import { useRef, useEffect, useState } from "react";
import { formatFileSize, getFileType, checkFileExtensions } from "@/utils/file-upload";
import { useStopChatMutation } from "@/hooks/services/mutations/ai-agent/use-stop-chat-mutation";
import Spinner from "../shared/spinner";
import { toast } from "sonner";
import CustomToast from "../shared/custom-toast";
import useUserQuery from "@/hooks/services/queries/user/use-user-query";
import useChatStreamActiveQuery from "@/hooks/services/queries/ai-agent/use-chat-stream-active-query";
import { useChatsQuery } from "@/hooks/services/queries/ai-agent/use-chats-query";

const allowedExtensions = ["pdf"];

const MessageInput = ({
  isHome,
  onSend,
  chatId,
  autoFocus = false,
}: {
  isHome: boolean;
  onSend: (text: string, files?: File[]) => void;
  chatId: string;
  autoFocus?: boolean;
}) => {
  const {
    attachments,
    setAttachments,
    removeAttachment,
    message,
    setMessage,
    selectedJob,
    clearSelectedJob,
  } = useAiAgentStore();

  const setEventType = useAiAgentStore((state) => state.setEventType);
  const setProgressList = useAiAgentStore((state) => state.setProgressList);
  // 현재 스트리밍 중인 채팅방 ID 목록
  const streamingChatIds = useAiAgentStore((state) => state.streamingChatIds);
  const showFileUploadError = useAiAgentStore(
    (state) => state.showFileUploadError
  );
  const setShowFileUploadError = useAiAgentStore(
    (state) => state.setShowFileUploadError
  );

  const { mutate: stopChat, isPending: isStopChatPending } =
    useStopChatMutation();

  const { data: userData } = useUserQuery();
  const { data: chatsData } = useChatsQuery();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isSending, setIsSending] = useState(false);
  // 현재 채팅방이 스트리밍 중인지 확인
  const isThisChatStreaming = streamingChatIds.has(chatId);
  const { data: streamActiveData } = useChatStreamActiveQuery(chatId, !isThisChatStreaming);
  const isServerStreaming = streamActiveData?.active === true;
  // 크레딧 부족 여부 (role 1이고 availableCredit < 1)
  const hasNoCredit = userData?.role === 1 && (userData?.availableCredit ?? 0) < 1;
  // 새 채팅(홈 또는 temp): 0원이어도 입력/전송 가능. 기존 채팅방에서만 0원일 때 전송 불가
  const isNewChat = isHome || !chatId || chatId.startsWith("temp");
  const disableDueToCredit = hasNoCredit && !isNewChat;
  // 타인의 채팅방 여부: 채팅 목록의 userId와 내 id가 다르면 입력 불가
  const currentChat = chatsData?.pages.flatMap((p) => p.content).find((c) => c.chatId === chatId);
  const chatUserId = currentChat?.userId;
  const myId = userData?.id;
  const isOtherUserChat =
    chatUserId != null &&
    chatUserId !== "" &&
    myId != null &&
    String(chatUserId) !== String(myId);
  const isDisabled = isThisChatStreaming || isServerStreaming || disableDueToCredit || isOtherUserChat;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    if (checkFileExtensions(files, allowedExtensions)) {
      toast.custom(
        () => (
          <CustomToast
            type="error"
            text="Unsupported file format(PDF only)."
          />
        ),
        {
          position: "bottom-center",
        }
      );
      event.target.value = "";
      return;
    }

    const newAttachments = Array.from(files).map((file, index) => {
      return {
        id: Date.now() + index, // 고유한 숫자 ID 생성
        s3Url: URL.createObjectURL(file),
        originalName: file.name,
        filesize: formatFileSize(file.size),
        type: getFileType(file),
        fileName: file.name,
        imageUrl: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : "",
        file: file, // multipart/form-data 전송을 위한 실제 File 객체 저장
      };
    });

    setAttachments([...attachments, ...newAttachments]);

    // 파일 input 초기화 (같은 파일을 다시 선택할 수 있도록)
    event.target.value = "";
  };
   const handleRemoveJob = () => {
    clearSelectedJob();
  };

  const handleSendMessage = async () => {
    const trimmedMessage = message.trim();
    if (isSending || !trimmedMessage) return;

    setIsSending(true);
    const messageToSend = trimmedMessage;
    setMessage(""); // 전송 즉시 입력창 비우기

    // attachments에서 File 객체 추출
    const files = attachments
      .map((attachment) => attachment.file)
      .filter((file): file is File => file !== undefined);

    try {
      onSend(messageToSend, files.length > 0 ? files : undefined);
      // 전송 후 attachments, selectedJob 초기화
      setAttachments([]);
      clearSelectedJob();
    } catch (error) {
      console.error("Failed to send message:", error);
      // 에러 발생 시 메시지 복구
      setMessage(messageToSend);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // IME 입력 중(한글, 일본어 등)에는 이벤트 무시
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    } else if (event.key === "Backspace" && message === "" && selectedJob != null) {
      // 텍스트가 없고 선택된 Job이 있을 때 backspace로 Job 제거
      event.preventDefault();
      clearSelectedJob();
    }
  };
  // 입력 영역 클릭 시 textarea에 포커스 및 커서를 끝으로 이동
  const handleTextareaClick = (e: React.MouseEvent) => {
    // textarea 자체 클릭이면 무시
    if ((e.target as HTMLElement).tagName === "TEXTAREA") return;

    textareaRef.current?.focus();
    textareaRef.current?.setSelectionRange(message.length, message.length);
  };

  const handleStopChat = () => {
    stopChat(chatId);
  };
  // textarea 높이 자동 조절
  const adjustTextareaHeight = () => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  };

  // message 변경 시 textarea 높이 조절
  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  // 컴포넌트 언마운트 시에만 스트리밍 상태 초기화
  useEffect(() => {
    return () => {
      setEventType("idle");
      setProgressList([]);
    };
  }, [setEventType, setProgressList]);

  // 파일 업로드 관련 SSE 에러 토스트
  useEffect(() => {
    if (showFileUploadError) {
      toast.custom(
        () => (
          <CustomToast
            type="error"
            text="File upload failed. Please try again."
          />
        ),
        {
          position: "bottom-center",
        }
      );
      setShowFileUploadError(false);
    }
  }, [showFileUploadError, setShowFileUploadError]);

  return (
    <div
      className="relative w-full max-w-[724px] overflow-hidden rounded-[16px]"
      style={{
        boxShadow: isHome
          ? "0px 4px 10px 0px #3737370D"
          : "0 4px 10px 0 rgba(55, 55, 55, 0.05)",
        backdropFilter: isHome ? "blur(8px)" : undefined,
        WebkitBackdropFilter: isHome ? "blur(8px)" : undefined,
      }}
    >
      <div
        className="flex flex-col px-1 pb-1 w-full"
        style={{
          background: isHome ? "#FFFFFF80" : "rgba(255, 255, 255, 0.95)",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.8)",
          minHeight: isHome ? "119px" : undefined,
          gap: isHome ? undefined : "32px",
          justifyContent: isHome ? "space-between" : undefined,
        }}
      >
        {attachments?.length > 0 && (
          <div className="px-5 pt-2 flex gap-[10px]">
            {attachments?.map((attachment) => (
              <AttachBox
                key={attachment.id}
                {...attachment}
                onRemove={removeAttachment}
              />
            ))}
          </div>
        )}
        {selectedJob != null && (
          <div className="px-5 pt-3 pb-1">
            <JobTag
              job={selectedJob.analysisNo}
              onRemove={() => clearSelectedJob()}
            />
          </div>
        )}
        <div
          className="min-h-[60px] px-5 pt-4 pb-2 cursor-text flex flex-wrap items-start gap-1"
          onClick={handleTextareaClick}
          style={{
            background: "transparent",
          }}
        >
          <textarea
            ref={textareaRef}
            placeholder={isOtherUserChat ? "Only the owner can send messages." : "Ask CellKey AI"}
            autoFocus={autoFocus}
            className="resize-none border-none outline-none flex-1 min-w-[200px] overflow-y-auto"
            style={{
              background: "transparent",
              minHeight: "24px",
              maxHeight: "380px",
            }}
            onKeyDown={handleKeyDown}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              adjustTextareaHeight();
            }}
            rows={1}
            disabled={isSending || isDisabled}
          />
          </div>
        <div className="flex gap-[10px] pl-3 pr-2 justify-between pb-1">
          <div className="flex gap-2">
            <label
              className={`${isDisabled ? "cursor-not-allowed" : "cursor-pointer"} w-[40px] h-[40px] flex items-center justify-center rounded-[8px]`}
              style={{
                background: "#FFFFFF80",
                boxShadow: "0px 4px 10px 0px #3737370D",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
            >
              <input
                type="file"
                hidden
                multiple
                accept={allowedExtensions.map((ext) => `.${ext}`).join(",")}
                onChange={handleFileChange}
                disabled={isDisabled}
              />
              <AttachIcon color={isDisabled ? "#B3B3B3" : "#4D4D4D"} />
            </label>
            <div
              className="rounded-[8px]"
              style={{
                background: "#FFFFFF80",
                boxShadow: "0px 4px 10px 0px #3737370D",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
            >
              <SourceSelectPopover
                chatId={chatId || "temp"}
                disabled={isDisabled}
              />
            </div>
          </div>
          <div className="w-[40px] h-[40px] flex items-center justify-center">
            {!isOtherUserChat && (isThisChatStreaming || isServerStreaming) ? (
              <button
                onClick={handleStopChat}
                className="bg-[#FFEAEC] rounded-full text-white w-[28px] h-[28px] flex items-center justify-center hover:bg-primary-60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="rounded-[2px] bg-red-30 w-3 h-3"></div>
              </button>
            ) : isOtherUserChat ? (
              <button
                disabled
                className="bg-primary-50 rounded-full text-white w-[28px] h-[28px] flex items-center justify-center cursor-not-allowed"
              >
                <LockIcon />
              </button>
            ) : (
              <button
                onClick={handleSendMessage}
                disabled={isSending || !message.trim() || isDisabled}
                className="bg-primary-50 rounded-full text-white w-[28px] h-[28px] flex items-center justify-center hover:bg-primary-60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
      {isStopChatPending && <Spinner />}
    </div>
  );
};

export default MessageInput;
